import type { BundleLine, CartLine } from '@/stores/cart.store';

/**
 * Stand-in slots for a cart QUOTE, never for a sale.
 *
 * `POST /sale/quote` refuses a slot-required ticket with no slot, and on the
 * Jual page the cashier has not picked any yet — so the cart could not show
 * the discounts checkout will give until the Details step. To quote earlier,
 * every slot-required ticket, add-on and bundle component that has no slot is
 * priced against a stand-in: the first bookable slot of that product on the
 * visit date. The quote never reserves capacity, so nothing is held.
 *
 * The figure is an ESTIMATE until every slot is picked: a time-of-day rate or a
 * Buy-X-Get-Y limited to a slot window prices the real slot differently. The
 * Details and Checkout steps re-quote with the cashier's own picks.
 */

/** Product ids that need a stand-in slot (a slot-required item with none picked). */
export function productsNeedingStandIn(lines: CartLine[], bundleLines: BundleLine[]): string[] {
  const ids = new Set<string>();
  for (const l of lines) {
    if (l.requiresSlot && l.seats.some((s) => !s.slotId)) ids.add(l.productId);
    // An add-on with no slot is one whose ride needs a slot but has none yet;
    // an add-on that needs no slot never carries one, so it is asked about too
    // and simply gets no stand-in back.
    for (const s of l.seats) for (const a of s.addOns) if (!a.slotId) ids.add(a.productId);
  }
  for (const b of bundleLines) {
    for (const c of b.components) if (!c.slotId) ids.add(c.productId);
    for (const a of b.extraAddOns) if (!a.slotId) ids.add(a.productId);
  }
  return [...ids];
}

export interface StandInResult {
  lines: CartLine[];
  bundleLines: BundleLine[];
  /** At least one stand-in was used — the quote is an estimate. */
  usedStandIn: boolean;
  /** A slot-required ticket has no slot and no stand-in yet — cannot quote. */
  blocked: boolean;
}

/**
 * Fill every missing slot from `standInFor(productId)`. A slot-required main
 * ticket or bundle component with no stand-in blocks the quote (the backend
 * would refuse it). An add-on with none is left as it is: most rides need no
 * slot, and one that does is refused by the backend just the same as before.
 */
export function withStandInSlots(
  lines: CartLine[],
  bundleLines: BundleLine[],
  standInFor: (productId: string) => string | undefined,
): StandInResult {
  let usedStandIn = false;
  let blocked = false;

  const fill = <T extends { slotId?: string }>(item: T, productId: string, required: boolean): T => {
    if (item.slotId) return item;
    const slotId = standInFor(productId);
    if (!slotId) {
      if (required) blocked = true;
      return item;
    }
    usedStandIn = true;
    return { ...item, slotId };
  };

  const outLines = lines.map((l) => ({
    ...l,
    seats: l.seats.map((s) => ({
      ...(l.requiresSlot ? fill(s, l.productId, true) : s),
      addOns: s.addOns.map((a) => fill(a, a.productId, false)),
    })),
  }));
  const outBundles = bundleLines.map((b) => ({
    ...b,
    components: b.components.map((c) => fill(c, c.productId, true)),
    extraAddOns: b.extraAddOns.map((a) => fill(a, a.productId, false)),
  }));

  return { lines: outLines, bundleLines: outBundles, usedStandIn, blocked };
}
